<template>
  <div class="app-header">
    <!-- Header Background Gradient -->
    <div class="header-background"></div>
    
    <div class="header-content">
      <!-- Left Section -->
      <div class="header-left">
        <!-- Logo Section -->
        <div class="logo" :class="{ 'collapsed': isMenuCollapsed }" @click="navigateToPlaytimeWebsite">
          <img 
            src="/img/playtime_logo.png"
            alt="Playtime Logo" 
          />
        </div>
        
        <!-- Modern Breadcrumb Navigation -->
        <nav class="breadcrumb-nav" aria-label="Breadcrumb">
          <ol class="breadcrumb-list">
            <li 
              v-for="(crumb, index) in breadcrumbs" 
              :key="index" 
              class="breadcrumb-item"
              :class="{ 'is-current': index === breadcrumbs.length - 1 }"
            >
              <!-- Modern Separator -->
              <div v-if="index > 0" class="breadcrumb-separator">
                <font-awesome-icon icon="chevron-right" />
              </div>
              
              <!-- Breadcrumb Link -->
              <button
                v-if="crumb.path && index !== breadcrumbs.length - 1"
                @click="navigateTo(crumb.path)"
                class="breadcrumb-link"
                :title="crumb.tooltip || crumb.text"
              >
                <div class="breadcrumb-icon" v-if="crumb.icon">
                  <font-awesome-icon :icon="crumb.icon" />
                </div>
                <span class="breadcrumb-text">{{ crumb.text }}</span>
              </button>
              
              <!-- Current Page -->
              <div 
                v-else 
                class="breadcrumb-current"
                :title="crumb.tooltip || crumb.text"
              >
                <div class="breadcrumb-icon" v-if="crumb.icon">
                  <font-awesome-icon :icon="crumb.icon" />
                </div>
                <span class="breadcrumb-text">{{ crumb.text }}</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>

      <!-- Right Section -->
      <div class="header-right">
        <!-- User Profile Section with Dropdown -->
        <div class="user-profile-container" ref="dropdownRef">
          <div 
            class="user-profile" 
            @click="toggleDropdown"
            :class="{ 'is-active': isDropdownOpen }"
            role="button"
            tabindex="0"
            @keydown.enter="toggleDropdown"
            @keydown.space.prevent="toggleDropdown"
            aria-haspopup="true"
            :aria-expanded="isDropdownOpen"
            aria-label="User account menu"
          >
            <div class="user-avatar">
              <div v-if="userAvatarUrl" class="avatar-image">
                <img :src="userAvatarUrl" alt="Profile Avatar" />
              </div>
              <div v-else class="avatar-placeholder">
                <span class="avatar-initials">{{ userInitials || '?' }}</span>
              </div>
              <div class="avatar-status"></div>
            </div>
            <div class="user-info">
              <span class="user-name">My Account</span>
              <!-- <span class="user-role">{{ currentUserRole }}</span> -->
            </div>
            <div class="profile-arrow" :class="{ 'is-rotated': isDropdownOpen }">
              <font-awesome-icon icon="chevron-down" />
            </div>
          </div>

          <!-- Dropdown Menu -->
          <transition name="dropdown">
            <div 
              v-if="isDropdownOpen" 
              class="dropdown-menu"
              role="menu"
              aria-label="Account navigation menu"
            >              
              <div class="dropdown-items">
                <button
                  v-for="item in accountMenuItems"
                  :key="item.id"
                  class="dropdown-item"
                  @click="navigateToAccountSection(item)"
                  role="menuitem"
                  :aria-label="`Navigate to ${item.label}`"
                >
                  <div class="dropdown-item-icon">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <span class="dropdown-item-text">{{ item.label }}</span>
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BaseButton from '@/components/common/BaseButton.vue'

export default {
  name: 'AppHeader',
  
  components: {
    BaseButton
  },
  
  props: {
    currentWorkspaceName: {
      type: String,
      default: ''
    },
    userWorkspaces: {
      type: Array,
      default: () => []
    },
    currentWorkspaceId: {
      type: String,
      default: ''
    },
    isSwitchingWorkspace: {
      type: Boolean,
      default: false
    },
    currentUserRole: {
      type: String,
      default: ''
    },
    userPlan: {
      type: String,
      default: 'FREE'
    },
    userInitials: {
      type: String,
      default: ''
    },
    userAvatarUrl: {
      type: String,
      default: null
    },
    isMenuCollapsed: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['workspace-change', 'select-plan'],
  
  setup(props) {
    const route = useRoute()
    const router = useRouter()
    
    // Reactive state for app name
    const currentAppName = ref('')
    
    // Dropdown state
    const isDropdownOpen = ref(false)
    const dropdownRef = ref(null)

    // Navigation methods
    const navigateToPlaytimeWebsite = () => {
      window.open('https://playtime-mobile.com/', '_blank')
    }

    const navigateTo = (path) => {
      router.push(path)
    }

    const navigateToMyAccount = () => {
      router.push('/my-account')
    }

    // Dropdown methods
    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value
    }

    const closeDropdown = () => {
      isDropdownOpen.value = false
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        closeDropdown()
      }
    }

    // Account navigation options based on MyAccount tabs
    const accountMenuItems = [
      { id: 'overview', label: 'Account Overview', icon: 'chart-line', path: '/my-account?tab=overview' },
      { id: 'plans', label: 'My Plans', icon: 'crown', path: '/my-account?tab=plans' },
      { id: 'profile', label: 'My Profile', icon: 'user', path: '/my-account?tab=profile' },
      { id: 'change-password', label: 'Change Password', icon: 'lock', path: '/my-account?tab=change-password' },
      { id: 'billing-details', label: 'Billing Details', icon: 'credit-card', path: '/my-account?tab=billing-details' },
      { id: 'invoices', label: 'Invoices', icon: 'receipt', path: '/my-account?tab=invoices' },
      { id: 'settings', label: 'Settings', icon: 'cog', path: '/my-account?tab=settings' }
    ]

    const navigateToAccountSection = (menuItem) => {
      router.push(menuItem.path)
      closeDropdown()
    }

    // Helper function to get app name from API
    const getAppName = async (appId) => {
      if (!appId) return 'New App'
      
      try {
        // Try to get app data from API
        const AppService = (await import('@/services/AppService')).default
        const response = await AppService.getAppConfigurationSummary(appId, props.currentWorkspaceId)
        return response?.title.length > 50 ? response?.title.substring(0, 50) + '...' : response?.title || `App ${appId}`
      } catch (error) {
        console.warn('Could not fetch app name:', error)
        return `App ${appId}`
      }
    }

    // Load app name when on app configuration page
    const loadAppName = async () => {
      const path = route.path
      if (path.startsWith('/apps/') && route.params.appId) {
        const appId = route.params.appId
        currentAppName.value = await getAppName(appId)
      } else {
        currentAppName.value = ''
      }
    }

    // Watch for route changes to update app name
    watch(() => route.path, loadAppName, { immediate: true })

    // Lifecycle hooks for dropdown
    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    // Dynamic breadcrumb generation
    const breadcrumbs = computed(() => {
      const path = route.path
      const routeName = route.name
      const query = route.query
      
      // Handle different route contexts
      if (path.startsWith('/my-account')) {
        return generateAccountBreadcrumbs(path, routeName)
      } else if (path.startsWith('/apps')) {
        return generateAppsBreadcrumbs(path, routeName, query)
      } else {
        return generateWorkspaceBreadcrumbs(path, routeName, props.currentWorkspaceName)
      }
    })

    // Generate breadcrumbs for My Account section
    const generateAccountBreadcrumbs = (path, routeName) => {
      const crumbs = [
        {
          text: props.currentWorkspaceName || 'Workspace',
          path: '/dashboard',
          icon: 'home',
          tooltip: `Return to ${props.currentWorkspaceName || 'Workspace'} Dashboard`
        }
      ]

      if (path === '/my-account') {
        crumbs.push({
          text: 'My Account',
          icon: 'user-circle'
        })
      } else {
        crumbs.push(
          {
            text: 'My Account',
            path: '/my-account',
            icon: 'user-circle'
          },
          {
            text: getAccountPageName(routeName),
            icon: getAccountPageIcon(routeName)
          }
        )
      }

      return crumbs
    }

    // Generate breadcrumbs for Apps section
    const generateAppsBreadcrumbs = (path, routeName, query) => {
      const crumbs = [
        {
          text: props.currentWorkspaceName || 'Workspace',
          path: '/dashboard',
          icon: 'home',
          tooltip: `Return to ${props.currentWorkspaceName || 'Workspace'} Dashboard`
        }
      ]

      if (path === '/apps') {
        crumbs.push({
          text: 'Apps',
          icon: 'mobile-alt'
        })
      } else if (path === '/apps/create') {
        crumbs.push(
          {
            text: 'Apps',
            path: '/apps',
            icon: 'mobile-alt'
          },
          {
            text: query.appId ? 'Edit App' : 'New App',
            icon: 'plus-circle'
          }
        )
      } else if (path.startsWith('/apps/') && path.endsWith('/stats')) {
        // App analytics/stats view
        crumbs.push(
          {
            text: 'Apps',
            path: '/apps',
            icon: 'mobile-alt'
          },
          {
            text: `${currentAppName.value || 'App'} Analytics`,
            icon: 'chart-bar',
            tooltip: 'App Analytics and Statistics'
          }
        )
      } else if (path.startsWith('/apps/')) {
        // App configuration view
        crumbs.push(
          {
            text: 'Apps',
            path: '/apps',
            icon: 'mobile-alt'
          },
          {
            text: currentAppName.value || 'App Configuration',
            icon: 'cog',
            tooltip: 'App Configuration and Settings'
          }
        )
      }

      return crumbs
    }

    // Generate breadcrumbs for Workspace section
    const generateWorkspaceBreadcrumbs = (path, routeName, workspaceName) => {
      const crumbs = []

      if (path === '/dashboard') {
        crumbs.push({
          text: workspaceName || 'Workspace',
          icon: 'home'
        })
      } else if (path === '/my-workspaces') {
        crumbs.push(
          {
            text: workspaceName || 'Workspace',
            path: '/dashboard',
            icon: 'home',
            tooltip: `Return to ${workspaceName || 'Workspace'} Dashboard`
          },
          {
            text: 'My Workspaces',
            icon: 'building'
          }
        )
      } else {
        // Other workspace-related pages
        crumbs.push({
          text: workspaceName || 'Workspace',
          path: '/dashboard',
          icon: 'home',
          tooltip: `Return to ${workspaceName || 'Workspace'} Dashboard`
        })
      }

      return crumbs
    }

    // Helper functions
    const getAccountPageName = (routeName) => {
      const pageNames = {
        'AccountOverview': 'Account Overview',
        'MyPlans': 'My Plans',
        'MyProfile': 'My Profile',
        'ChangePassword': 'Change Password',
        'BillingDetails': 'Billing Details',
        'Invoices': 'Invoices',
        'Settings': 'Settings'
      }
      return pageNames[routeName] || 'Account'
    }

    const getAccountPageIcon = (routeName) => {
      const pageIcons = {
        'AccountOverview': 'chart-line',
        'MyPlans': 'crown',
        'MyProfile': 'user',
        'ChangePassword': 'lock',
        'BillingDetails': 'credit-card',
        'Invoices': 'receipt',
        'Settings': 'cog'
      }
      return pageIcons[routeName] || 'file'
    }

    // Role formatting methods (existing)
    const formatRole = (role) => {
      const roleNames = {
        'OWNER': 'Owner',
        'ADMIN': 'Admin',
        'MEMBER': 'Member',
        'VIEWER': 'Viewer'
      }
      return roleNames[role] || role
    }

    const getRoleClass = (role) => {
      const roleClasses = {
        'OWNER': 'role-owner',
        'ADMIN': 'role-admin',
        'MEMBER': 'role-member',
        'VIEWER': 'role-viewer'
      }
      return roleClasses[role] || 'role-default'
    }
    
    return {
      breadcrumbs,
      navigateToPlaytimeWebsite,
      navigateTo,
      navigateToMyAccount,
      formatRole,
      getRoleClass,
      isDropdownOpen,
      dropdownRef,
      toggleDropdown,
      closeDropdown,
      handleClickOutside,
      accountMenuItems,
      navigateToAccountSection
    }
  }
}
</script>

<style scoped>
/* Modern Header Styles */
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--header-height); /* 64px */
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  display: flex;
  align-items: center;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%);
  pointer-events: none;
}

.header-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--component-padding) 0 0; /* 24px right, 0 left */
}

/* Left Section */
.header-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl); /* 32px */
}

/* Logo Section */
.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding-left: 16px; /* Aligns with side panel */
}

.logo.collapsed {
  padding-left: 10px;
}

.logo:hover {
  opacity: 0.8;
}

.logo img {
  height: 32px;
  width: auto;
}

/* Modern Breadcrumb Navigation */
.breadcrumb-nav {
  display: flex;
  align-items: center;
}

.breadcrumb-list {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: var(--spacing-xs); /* 4px */
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs); /* 4px */
}

.breadcrumb-separator {
  display: flex;
  align-items: center;
  color: #94a3b8;
  font-size: 10px;
  margin: 0 var(--spacing-xs);
}

.breadcrumb-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 8px;
  background: none;
  border: none;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
}

.breadcrumb-link:hover {
  background: rgba(107, 114, 128, 0.1);
  color: #4b5563;
  transform: translateY(-1px);
}

.breadcrumb-current {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 8px;
  background: rgba(107, 114, 128, 0.1);
  color: #4b5563;
  font-size: 14px;
  font-weight: 600;
}

.breadcrumb-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.breadcrumb-icon i {
  font-size: 12px;
}

.breadcrumb-text {
  white-space: nowrap;
}

/* Right Section */
.header-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg); /* 24px */
}

.user-role-badge {
  display: flex;
  align-items: center;
}

.role-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.role-badge.role-owner {
  background: #fdf4ff;
  color: #a21caf;
  border: 1px solid #f3e8ff;
}

.role-badge.role-admin {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #dbeafe;
}

.role-badge.role-member {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #dcfce7;
}

.role-badge.role-viewer {
  background: #f9fafb;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}

.role-badge.role-default {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

/* User Profile Section with Dropdown */
.user-profile-container {
  position: relative;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
}

.user-profile:hover,
.user-profile.is-active {
  background: rgba(59, 130, 246, 0.05);
  border-color: rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.user-avatar {
  position: relative;
  width: 36px;
  height: 36px;
}

.avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.avatar-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.avatar-initials {
  color: white;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
}

.avatar-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: #16a34a;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.5);
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1;
}

.user-role {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1;
}

.profile-arrow {
  display: flex;
  align-items: center;
  color: #94a3b8;
  font-size: 10px;
  transition: transform 0.2s ease;
}

.profile-arrow.is-rotated {
  transform: rotate(180deg);
}

.user-profile:hover .profile-arrow:not(.is-rotated) {
  transform: translateY(1px);
}

/* Dropdown Menu Styles */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(226, 232, 240, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 1001;
  overflow: hidden;
}

.dropdown-header {
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.dropdown-user-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.dropdown-avatar {
  width: 48px;
  height: 48px;
  position: relative;
}

.dropdown-avatar .avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dropdown-avatar .avatar-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dropdown-avatar .avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.dropdown-avatar .avatar-initials {
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;
}

.dropdown-user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dropdown-user-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.2;
}

.dropdown-user-role {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.dropdown-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(226, 232, 240, 0.8) 50%, transparent 100%);
  margin: 0 var(--spacing-sm);
}

.dropdown-items {
  padding: var(--spacing-sm) 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-lg);
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
}

.dropdown-item:hover {
  background: rgba(59, 130, 246, 0.05);
  color: #1e293b;
  transform: translateX(4px);
}

.dropdown-item:active {
  background: rgba(59, 130, 246, 0.1);
  transform: translateX(2px);
}

.dropdown-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #64748b;
  transition: color 0.2s ease;
}

.dropdown-item:hover .dropdown-item-icon {
  color: #3b82f6;
}

.dropdown-item-icon i {
  font-size: 14px;
}

.dropdown-item-text {
  flex: 1;
  white-space: nowrap;
}

/* Dropdown Transitions */
.dropdown-enter-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-leave-active {
  transition: all 0.15s cubic-bezier(0.4, 0, 1, 1);
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.plan-section {
  display: flex;
  align-items: center;
}

.plan-section span {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* User Avatar Styles */
.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;
  border-radius: 50%;
}

.user-avatar:hover {
  transform: scale(1.05);
  opacity: 0.9;
}

.user-avatar .avatar-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #e1e5e9;
}

.user-avatar .avatar-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar .avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  transition: background 0.2s ease;
}

.user-avatar:hover .avatar-placeholder {
  background: linear-gradient(135deg, #0056b3, #003d82);
}

.plan-badge {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.plan-badge.free {
  background: transparent;
  color: #d97706;
}

.plan-badge.current {
  background: #d1fae5;
  color: #065f46;
}


@media (max-width: 768px) {
  .header-actions {
    display: none;
  }

  .header-left {
    gap: 12px;
  }

  .breadcrumb-nav {
    margin-left: 12px;
  }

  .breadcrumb-link,
  .breadcrumb-current {
    font-size: 13px;
    padding: 3px 6px;
  }

  .breadcrumb-link i,
  .breadcrumb-current i {
    font-size: 11px;
  }

  .breadcrumb-separator {
    font-size: 10px;
  }

  /* Hide some breadcrumb text on mobile, keep icons */
  .breadcrumb-link span:not(.breadcrumb-separator),
  .breadcrumb-current span:not(.breadcrumb-separator) {
    display: none;
  }
}

/* Modern Responsive Design */
@media (max-width: 768px) {
  .header-content {
    padding: 0 var(--spacing-md) 0 0; /* 16px right, 0 left */
  }
  
  .header-left {
    gap: var(--spacing-md); /* 16px */
  }
  
  .logo {
    padding-left: 18px; /* Aligns with side panel on mobile */
  }
  
  .logo img {
    height: 28px;
  }
  
  .breadcrumb-nav {
    display: none;
  }
  
  .user-info {
    display: none;
  }
  
  .profile-arrow {
    display: none;
  }

  /* Dropdown adjustments for mobile */
  .dropdown-menu {
    min-width: 260px;
    right: -8px;
  }

  .dropdown-header {
    padding: var(--spacing-md);
  }

  .dropdown-user-name {
    font-size: 15px;
  }

  .dropdown-item {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .user-profile {
    padding: var(--spacing-xs);
  }
  
  .user-avatar {
    width: 32px;
    height: 32px;
  }

  /* Further dropdown adjustments for small screens */
  .dropdown-menu {
    min-width: 240px;
    right: -12px;
  }

  .dropdown-avatar {
    width: 40px;
    height: 40px;
  }

  .dropdown-avatar .avatar-initials {
    font-size: 16px;
  }

  .dropdown-item {
    padding: 10px var(--spacing-sm);
    font-size: 12px;
  }

  .dropdown-item-icon {
    width: 18px;
    height: 18px;
  }

  .dropdown-item-icon i {
    font-size: 12px;
  }
}

/* Focus States for Accessibility */
.logo:focus,
.breadcrumb-link:focus,
.user-profile:focus,
.dropdown-item:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.dropdown-item:focus {
  background: rgba(59, 130, 246, 0.05);
  color: #1e293b;
}

/* Animation for smooth interactions */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-content {
  animation: fadeInUp 0.3s ease-out;
}
</style>
